import { useEffect } from "react";
import Lenis from "lenis";

/**
 * Smooth-scroll bootstrapper. Mounts Lenis once at the app root, drives a
 * single shared RAF loop, and tears everything down on unmount. SSR-safe:
 * all browser-only work is gated inside useEffect, so the prerender pass
 * (Puppeteer) never sees Lenis or requestAnimationFrame.
 *
 * Honors `prefers-reduced-motion: reduce` — when the user opts out of
 * motion we skip Lenis entirely and let the browser's native scroll do
 * its thing instead of forcing a custom curve.
 */
const LenisProvider = ({ children }: { children: React.ReactNode }) => {
    useEffect(() => {
        if (typeof window === "undefined") return;

        const prefersReduced = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        if (prefersReduced) return;

        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
        });

        let frameId = 0;
        const raf = (time: number) => {
            lenis.raf(time);
            frameId = requestAnimationFrame(raf);
        };
        frameId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(frameId);
            lenis.destroy();
        };
    }, []);

    return <>{children}</>;
};

export default LenisProvider;
